import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';

const DEMO_ACCOUNTS = [
  { label: 'Worker Demo', email: 'james.morrison@shiftmatch.app', password: 'worker123', color: 'chip-green' },
  { label: 'Employer Demo', email: 'employer1@shiftmatch.app', password: 'employer123', color: 'chip-blue' },
  { label: 'Admin Demo', email: 'admin@shiftmatch.app', password: 'admin123', color: 'chip-neutral' },
];

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return setError('Email and password required');
    setLoading(true); setError('');
    try {
      const r = await axios.post('/api/auth/login', { email, password });
      const { token, user } = r.data;
      if (onLogin) onLogin({ token, user });
      const target = user.role === 'admin' ? '/admin' : user.role === 'employer' ? '/employer' : '/worker';
      navigate(target);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
    setLoading(false);
  };

  const fillDemo = (email, password) => {
    setEmail(email);
    setPassword(password);
  };

  return (
    <div className="min-h-screen flex">
      <SEOHelmet title="Log in — ShiftMatch" />

      {/* ── LEFT: Brand Panel ── */}
      <div className="hidden lg:flex w-1/2 gradient-hero relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(16,185,129,0.15)_0%,transparent_60%)]" />
        <div className="absolute top-20 left-20 w-40 h-40 border border-white/5 rounded-full animate-float opacity-10" />
        <div className="absolute bottom-20 right-20 w-24 h-24 border border-white/10 rounded-2xl rotate-12 animate-float opacity-20" style={{ animationDelay: '-3s' }} />
        <div className="relative text-center">
          <img src="/logo-icon.svg" alt="ShiftMatch" className="w-16 h-16 mx-auto mb-6" />
          <h2 className="font-brand text-3xl font-extrabold text-white mb-3 tracking-tight">Welcome back</h2>
          <p className="text-white/50 text-sm max-w-xs mx-auto">Find flexible shifts. Get paid fast. It's that simple.</p>
          <div className="mt-8 flex items-center justify-center gap-1.5 text-white/30 text-[11px]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60" /> Now live across Australia
          </div>
        </div>
      </div>

      {/* ── RIGHT: Login Form ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-sm">
          {/* Brand (mobile) */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <img src="/logo-icon.svg" alt="" className="w-7 h-7" />
            <span className="font-brand font-bold text-sm text-neutral-900">ShiftMatch</span>
          </div>

          <h2 className="text-xl font-bold text-neutral-900 mb-1">Log in</h2>
          <p className="text-sm text-neutral-400 mb-8">Welcome back. Find your next shift.</p>

          {/* Demo account badges */}
          <div className="mb-6">
            <p className="text-[10px] text-neutral-400 font-semibold uppercase tracking-wider mb-2">Demo accounts</p>
            <div className="flex flex-wrap gap-1.5">
              {DEMO_ACCOUNTS.map(d => (
                <button key={d.label} onClick={() => fillDemo(d.email, d.password)}
                  className={`${d.color} text-[10px] px-2.5 py-1 rounded-full font-semibold cursor-pointer hover:opacity-80 transition`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && <div className="text-xs text-rose-600 bg-rose-50 px-3 py-2 rounded-lg font-medium">{error}</div>}

            <div>
              <label className="text-[11px] font-semibold text-neutral-600 mb-1.5 block">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input w-full px-4 py-3 text-sm" />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-neutral-600 mb-1.5 block">Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="input w-full px-4 py-3 text-sm" />
            </div>

            <button type="submit" disabled={loading}
              className="btn-primary w-full py-3 text-sm">
              {loading ? 'Logging in...' : 'Log in'}
            </button>
          </form>

          <div className="divider" />

          <p className="text-center text-xs text-neutral-400">
            New to ShiftMatch?{' '}
            <Link to="/register" className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
